/**
 * Formats a phone number for display by adding "+1 " prefix
 * Example: 12505550199 -> +1 2505550199
 * @param phone - Phone number string
 * @returns Formatted phone number with "+1 " prefix, or empty string if input is empty/null/undefined
 */
export const formatPhoneForDisplay = (phone: string | null | undefined): string => {
  if (!phone) return ''
  
  const trimmed = phone.trim()
  if (trimmed === '') return ''
  
  // Remove any existing +1 or +1 prefix and spaces, keep only digits
  const digitsOnly = trimmed.replace(/^\+1\s*/, '').replace(/\D/g, '')
  
  // If 11 digits starting with 1, format as +1 [rest]
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+1 ${digitsOnly.substring(1)}`
  }
  
  // If 10 digits, format as +1 [number]
  if (digitsOnly.length === 10) {
    return `+1 ${digitsOnly}`
  }
  
  // Otherwise return as is (already formatted or invalid)
  return trimmed
}

/**
 * Formats a phone number for saving:
 * - If input is "+1 2505550199" -> "12505550199" (remove +1 and space)
 * - If 10 digits only -> add "1" prefix -> "12505550199"
 * - If 11 digits starting with 1 -> save without space -> "12505550199"
 * @param phone - Phone number string
 * @returns Formatted phone number as 11 digits starting with 1, or empty string if input is empty/null/undefined
 */
export const formatPhoneForSave = (phone: string | null | undefined): string => {
  if (!phone) return ''
  
  const trimmed = phone.trim()
  // Return empty string if input is empty after trimming
  if (trimmed === '') return ''
  
  // Remove + symbol, spaces, and non-digit characters, keep only digits
  const digitsOnly = trimmed.replace(/\+/g, '').replace(/\s/g, '').replace(/\D/g, '')
  
  // If 10 digits, add "1" prefix
  if (digitsOnly.length === 10) {
    return `1${digitsOnly}`
  }
  
  // If 11 digits starting with 1, return without space
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return digitsOnly
  }
  
  // Otherwise return digits only (shouldn't normally happen, but handle gracefully)
  return digitsOnly
}

